import { z } from 'zod';

// Value Objects
export const MoneySchema = z.object({
  amount: z.number(),
  currency: z.string()
});

export const QuantitySchema = z.object({
  value: z.number(),
  unit: z.string()
});

// Entity Base
export const EntityBase = z.object({
  _id: z.string(),
  _rev: z.string().optional(),
  type: z.string(),
  version: z.number().int().min(1).default(1),
  createdAt: z.date(),
  updatedAt: z.date()
});

// Location Types
export const LocationType = z.enum(['pallet', 'shelf', 'bin', 'bulk', 'staging']);
export const LocationStatus = z.enum(['active', 'inactive', 'reserved', 'maintenance']);

// Entity Types
export const EntityType = z.enum(['location', 'product', 'warehouse', 'stock', 'movement', 'product_variant']);

export const LocationSchema = EntityBase.extend({
  warehouseId: z.string(),
  code: z.string().regex(/^[A-Z]-[0-9]{2}-[0-9]{2}-[0-9]{3}$/),
  // status: LocationStatus,
  // dimensions: z.object({
  //   length: z.number().positive(),
  //   width: z.number().positive(),
  //   height: z.number().positive(),
  // }),
  // capacity: z.object({
  //   maxWeight: z.number().positive(),
  //   maxPallets: z.number().int().min(0),
  // }),
  // restrictions: z.array(z.string()),
  parentId: z.string().optional().nullable(),
  // metadata: z.record(z.string(), z.string()).optional()
});

// Stock Types
export const StockStatus = z.enum(['available', 'reserved', 'damaged', 'expired', 'in_transit']);

export const StockSchema = EntityBase.extend({
  productId: z.string().uuid(),
  warehouseId: z.string().uuid(),
  locationId: z.string().uuid(),
  quantity: z.number().int().min(0),
  lastMovementDate: z.date(),
  lastCountDate: z.date().optional(),
  status: StockStatus,
  variantId: z.string().uuid().optional(),
  lotNumber: z.string().optional(),
  serialNumber: z.string().optional(),
  expirationDate: z.date().optional(),
  palletSpaces: z.number().int().min(1).optional(),
  weight: z.number().positive().optional(),
  dimensions: z.object({
    length: z.number().positive(),
    width: z.number().positive(),
    height: z.number().positive(),
  }).optional(),
  metadata: z.object({
    restrictions: z.array(z.string()).optional(),
    storageConditions: z.array(z.string()).optional(),
    notes: z.string().optional(),
  }).optional()
});

// Product Types
export const ProductSchema = EntityBase.extend({
  name: z.string(),
  sku: z.string(),
  description: z.string().optional(),
  barcode: z.string().optional(),
  category: z.string().optional(),
  cost: MoneySchema,
  price: MoneySchema,
  minQuantity: QuantitySchema,
  maxQuantity: QuantitySchema.optional(),
  isActive: z.boolean().default(true)
});

// Product Variant Types
export const ProductAttributeSchema = z.object({
  name: z.string(),
  value: z.string(),
});

export const ProductVariantSchema = EntityBase.extend({
  productId: z.string(),
  sku: z.string(),
  barcode: z.string().optional(),
  attributes: z.array(ProductAttributeSchema),
  price: MoneySchema,
  cost: MoneySchema,
  minQuantity: QuantitySchema,
  maxQuantity: QuantitySchema.optional(),
  isActive: z.boolean().default(true)
});

// Warehouse Types
export const WarehouseStatuses = {
  ACTIVE: 'ACTIVE' as const,
  INACTIVE: 'INACTIVE' as const,
  MAINTENANCE: 'MAINTENANCE' as const
};
export type WarehouseStatus = typeof WarehouseStatuses[keyof typeof WarehouseStatuses];
export const WarehouseStatus = z.enum([
  WarehouseStatuses.ACTIVE,
  WarehouseStatuses.INACTIVE,
  WarehouseStatuses.MAINTENANCE
]);

export const WarehouseLocationSchema = z.object({
  address: z.string(),
  city: z.string(),
  state: z.string(),
  country: z.string(),
  postalCode: z.string(),
  coordinates: z.object({
    latitude: z.number(),
    longitude: z.number()
  }).optional()
});

export const WarehouseCapacitySchema = z.object({
  maxCapacity: z.number().positive(),
  usedCapacity: z.number().min(0),
  unit: z.string()
});

export const WarehouseSchema = EntityBase.extend({
  name: z.string(),
  status: WarehouseStatus,
  // code: z.string(),
  //location: WarehouseLocationSchema,
  //capacity: WarehouseCapacitySchema,
  // status: z.enum(['ACTIVE', 'INACTIVE', 'MAINTENANCE']).default('ACTIVE'),
  // parentId: z.string().uuid().optional(),
  // metadata: z.record(z.string(), z.unknown()).optional()
});

// Movement Types
export const MovementTypes = {
  IN: 'IN' as const,
  OUT: 'OUT' as const,
  TRANSFER: 'TRANSFER' as const,
  ADJUSTMENT: 'ADJUSTMENT' as const
};
export type MovementType = typeof MovementTypes[keyof typeof MovementTypes];
export const MovementTypeEnum = z.enum([
  MovementTypes.IN,
  MovementTypes.OUT,
  MovementTypes.TRANSFER,
  MovementTypes.ADJUSTMENT
]);

export const MovementStatusTypes = {
  PENDING: 'PENDING' as const,
  IN_PROGRESS: 'IN_PROGRESS' as const,
  COMPLETED: 'COMPLETED' as const,
  CANCELLED: 'CANCELLED' as const,
  FAILED: 'FAILED' as const
};
export type MovementStatus = typeof MovementStatusTypes[keyof typeof MovementStatusTypes];
export const MovementStatusEnum = z.enum([
  MovementStatusTypes.PENDING,
  MovementStatusTypes.IN_PROGRESS,
  MovementStatusTypes.COMPLETED,
  MovementStatusTypes.CANCELLED,
  MovementStatusTypes.FAILED
]);

export const MovementReferenceSchema = z.object({
  type: z.enum(['PO', 'SO', 'TO', 'ADJ']),
  number: z.string(),
  externalId: z.string().optional()
});

export const MovementSchema = EntityBase.extend({
  movementType: MovementTypeEnum,
  productId: z.string(),
  warehouseId: z.string(),
  quantity: QuantitySchema,
  unitCost: MoneySchema,
  unitPrice: MoneySchema.optional(),
  reference: MovementReferenceSchema.optional(),
  notes: z.string().optional(),
  destinationWarehouseId: z.string().optional(),
  status: MovementStatusEnum,
  reason: z.string().optional(),
  initiatedBy: z.string(),
  approvedBy: z.string().optional(),
  completedBy: z.string().optional(),
  cancelledBy: z.string().optional(),
  failedBy: z.string().optional(),
  fromLocationId: z.string().optional(),
  toLocationId: z.string().optional(),
});

// Types
export type Money = z.infer<typeof MoneySchema>;
export type Quantity = z.infer<typeof QuantitySchema>;
export type Entity = z.infer<typeof EntityBase>;

// Movement Types
export type Movement = z.infer<typeof MovementSchema>;
export type MovementReference = z.infer<typeof MovementReferenceSchema>;

// Location Types
export type Location = z.infer<typeof LocationSchema>;
export type LocationTypeEnum = z.infer<typeof LocationType>;
export type LocationStatusEnum = z.infer<typeof LocationStatus>;

// Stock Types
export type Stock = z.infer<typeof StockSchema>;
export type StockStatusEnum = z.infer<typeof StockStatus>;

// Product Types
export type Product = z.infer<typeof ProductSchema>;
export type ProductAttribute = z.infer<typeof ProductAttributeSchema>;
export type ProductVariant = z.infer<typeof ProductVariantSchema>;

// Warehouse Types
export type WarehouseLocation = z.infer<typeof WarehouseLocationSchema>;
export type WarehouseCapacity = z.infer<typeof WarehouseCapacitySchema>;
export type Warehouse = z.infer<typeof WarehouseSchema>;
export type WarehouseStatusEnum = z.infer<typeof WarehouseStatus>;
